import {
  formatGroupedNumber,
  type DeepPartial,
  type KLineOption,
  type KLinePoint,
  type KLineThemeInput,
} from '@wiki/kline/types'
import type { BrokerSymbol, CategoryTranslationMap, SymbolCategory } from '~/api/wikitrade-market'
import wikiBitLogoDark from '~/assets/market/wikibit-watermark-dark.png'
import wikiBitLogo from '~/assets/market/wikibit-watermark-light.png'

import { DEFAULT_SYMBOL, type Period } from './constants'

// 接口返回里价格可能是字符串、数字或空值；这里只接受可预期的基础类型参与数值格式化。
const toNumericText = (value: unknown) => {
  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return String(value)
  }

  return ''
}

const parseNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(toNumericText(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

const normalizeTimestamp = (timestamp: number) => {
  return timestamp > 0 && timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp
}

// 当品种配置没有给 price_precision 时，根据最后一个价格兜底推断小数位。
// 限制在 2-6 位之间，避免异常字符串把坐标轴和价格线撑得过长。
const inferPrecision = (value: unknown) => {
  const text = toNumericText(value)
  const decimalLength = text.includes('.') ? (text.split('.')[1]?.length ?? 2) : 2
  return Math.min(Math.max(decimalLength, 2), 6)
}

/** 图表主系列 ID，需与工作台缩放/价格线配置保持一致。 */
export const MAIN_SERIES_ID = 'wikitrade-main'

// 品种展示优先使用币种名称，缺少结算币时退化成单边名称或原始 symbol。
export const formatSymbolName = (symbol?: BrokerSymbol) => {
  if (!symbol) return DEFAULT_SYMBOL
  const base = symbol.base_currency_name || symbol.base_currency || symbol.symbol
  const settlement = symbol.settlement_currency_name || symbol.settlement_currency
  return settlement ? `${base}/${settlement}` : base
}

// 分类名称优先使用翻译接口结果；翻译缺失时按中文、通用名称、英文、code 逐级兜底。
export const getCategoryName = (category: SymbolCategory, translations?: CategoryTranslationMap | null) => {
  const translated = translations?.[category.code]
  if (translated) return translated
  return category.zh_name || category.name || category.en_name || category.code
}

// 不同聚合路由返回的分类字段名不完全一致，统一在这里做兼容，页面只关心最终 code。
export const getSymbolCategoryCode = (symbol: BrokerSymbol) => {
  const record = symbol as Record<string, unknown>
  const keys = [
    'symbol_code',
    'symbolCode',
    'symbol_category',
    'symbol_category_code',
    'symbolCategory',
    'symbolCategoryCode',
    'category_code',
    'categoryCode',
    'category',
    'group',
    'group_code',
    'groupCode',
    'symbol_type',
    'symbolType',
    'type',
    'instrument_type',
    'instrumentType',
  ]

  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' || typeof value === 'number') {
      const categoryCode = String(value).trim()
      if (categoryCode) return categoryCode
    }
  }

  return ''
}

// K 线组件的价格格式化、Y 轴精度和价格线精度都走同一个方法，避免展示口径不一致。
export const getPrecision = (symbol?: BrokerSymbol, fallbackValue?: unknown) => {
  const precision = parseNumber(symbol?.price_precision)
  return Number.isFinite(precision) ? Number(precision) : inferPrecision(fallbackValue)
}

// Element、K 线 tooltip 和价格轴共享这个格式化出口，异常值统一显示占位符。
export const formatPrice = (value: unknown, precision = 2) => {
  const number = parseNumber(value)
  return Number.isFinite(number) ? formatGroupedNumber(number, precision) : '--'
}

/** 格式化完整时间 (YYYY-MM-DD HH:mm)，用于产品详情休市更新文案。 */
export const formatFullTime = (timestamp?: number) => {
  if (!Number.isFinite(timestamp)) return '--'
  const date = new Date(timestamp as number)
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  const hour = `${date.getHours()}`.padStart(2, '0')
  const minute = `${date.getMinutes()}`.padStart(2, '0')
  return `${year}-${month}-${day} ${hour}:${minute}`
}

/** 根据主题输入判断是否暗色图表，用于切换 Logo 资源。 */
const isDarkChartTheme = (theme?: KLineThemeInput) => {
  if (!theme || typeof theme === 'string') return theme === 'dark'
  return theme.mode === 'dark' || theme.id === 'dark' || theme.extends === 'dark'
}

/**
 * WikiTrade 工作台相对包默认值的图表配置。
 * 接收 option factory context，以便暗黑模式切换 Logo。
 */
export const createChartOption = (context: { theme?: KLineThemeInput } = {}): DeepPartial<KLineOption> => {
  return {
    meta: {
      timezone: 'Asia/Shanghai',
    },
    // 使用 @wiki/kline LogoComponent（需在 createRuntime 中注册），绘制在主图 pane 内左下角，天然位于 x 轴上方。
    logo: {
      show: true,
      url: isDarkChartTheme(context.theme) ? wikiBitLogoDark : wikiBitLogo,
      position: 'bottom-left',
      opacity: 1,
      imageHeight: 22,
      offset: {
        x: 12,
        y: 12,
      },
    },
    xAxis: {
      line: {
        show: true,
      },
    },
    yAxis: {
      line: {
        show: false,
      },
    },
  }
}

type JsonRecord = Record<string, unknown>

const asRecord = (value: unknown): JsonRecord | undefined => {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : undefined
}

const readField = (value: unknown, key: string) => asRecord(value)?.[key]

const collectRecords = (value: unknown): JsonRecord[] => {
  const values = Array.isArray(value) ? value : [value]
  return values.map(asRecord).filter((item): item is JsonRecord => item !== undefined)
}

/** 从 WebSocket 消息中解析实时 K 线点（可带 symbol/period）。 */
export interface RealtimeKLinePoint {
  symbol?: string
  period?: string
  bar: KLinePoint
}

export const readRealtimePoints = (message: unknown): RealtimeKLinePoint[] => {
  const root = asRecord(message)
  const data = readField(root, 'data')
  const payload = readField(root, 'payload')
  const payloadBody = readField(payload, 'body')
  const body = readField(root, 'body')
  const candidates = [
    readField(data, 'k'),
    data,
    readField(payloadBody, 'content'),
    payloadBody,
    readField(body, 'content'),
    body,
    readField(root, 'kline'),
    readField(root, 'tick'),
    root,
  ].flatMap(collectRecords)

  return candidates.flatMap((source): RealtimeKLinePoint[] => {
    if (source.timestamp === undefined && source.t === undefined) return []
    const rawTime = parseNumber(source.timestamp ?? source.t)
    const time = Number.isFinite(rawTime) ? normalizeTimestamp(rawTime) : rawTime
    const open = parseNumber(source.open ?? source.open_price ?? source.o)
    const high = parseNumber(source.high ?? source.high_price ?? source.h)
    const low = parseNumber(source.low ?? source.low_price ?? source.l)
    const close = parseNumber(source.close ?? source.close_price ?? source.c)
    if (![time, open, high, low, close].every(Number.isFinite)) return []

    const point: KLinePoint & Record<string, unknown> = { time, open, high, low, close }
    const optionalNumber = (value: unknown) => {
      if (value === undefined || value === null || value === '') return undefined
      const parsed = parseNumber(value)
      return Number.isFinite(parsed) ? parsed : undefined
    }
    const extraFields: Array<[string, unknown]> = [
      [
        'volume',
        optionalNumber(
          source.volume ??
            source.vol ??
            source.v ??
            source.tick_volume ??
            source.trade_volume ??
            source.turnover_volume ??
            source.volume_value,
        ),
      ],
      [
        'volumeDelta',
        optionalNumber(source.volumeDelta ?? source.volume_delta ?? source.deltaVolume ?? source.delta_volume),
      ],
      ['buyVolume', optionalNumber(source.buyVolume ?? source.buy_volume ?? source.buyVol ?? source.buy_vol)],
      ['sellVolume', optionalNumber(source.sellVolume ?? source.sell_volume ?? source.sellVol ?? source.sell_vol)],
      ['inV', optionalNumber(source.inV)],
      ['outV', optionalNumber(source.outV)],
      ['state', optionalNumber(source.state)],
      ['tickVol', optionalNumber(source.tickVol ?? source.tick_vol)],
    ]
    extraFields.forEach(([key, value]) => {
      if (value !== undefined) point[key] = value
    })

    return [
      {
        symbol: typeof source.symbol === 'string' ? source.symbol : undefined,
        period: typeof source.period === 'string' ? source.period : undefined,
        bar: point,
      },
    ]
  })
}

export const readRealtimePoint = (message: unknown, symbol: string, period: Period): KLinePoint | null => {
  return (
    readRealtimePoints(message).find(
      (item) => (!item.symbol || item.symbol === symbol) && (!item.period || item.period === period),
    )?.bar || null
  )
}

// 实时 K 线优先覆盖同一根蜡烛，因为同一个周期内 WebSocket 会持续推送最新收盘价。
// 如果推来的是新周期蜡烛，则追加到尾部；乱序时再排序，保证组件拿到递增时间序列。
export const mergeRealtimePoint = (current: KLinePoint[], incoming: KLinePoint) => {
  const matchedIndex = current.findIndex((point) => point.time === incoming.time)
  if (matchedIndex >= 0) {
    return current.map((point, index) => (index === matchedIndex ? incoming : point))
  }

  if (!current.length || incoming.time > current[current.length - 1]!.time) {
    return [...current, incoming]
  }

  return [...current, incoming].toSorted((left, right) => left.time - right.time)
}
